import { useState, useEffect } from 'react';
import { ScrollText, CheckCircle2 } from 'lucide-react';
import ImageUploader from './ImageUploader';
import { communityTrust } from '../lib/api';
import { toast } from 'sonner';

/**
 * Wall-license photo uploader (Community Trust & Safety).
 *
 * "Snap a pic of your wall license so people know you are the real deal."
 * — displayed publicly on the Store Info page.
 */
export default function StoreLicenseUploader({ store, onSaved }) {
  const [url, setUrl] = useState('');
  const [number, setNumber] = useState('');
  const [authority, setAuthority] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setUrl(store?.license_photo_url || '');
    setNumber(store?.license_number || '');
    setAuthority(store?.license_authority || '');
  }, [store?.id, store?.license_photo_url, store?.license_number, store?.license_authority]);

  if (!store) return null;

  const save = async () => {
    if (!url) { toast.error('Upload the wall-license photo first'); return; }
    setBusy(true);
    try {
      await communityTrust.uploadLicensePhoto(store.id, {
        license_photo_url: url,
        license_number: number.trim() || undefined,
        license_authority: authority.trim() || undefined,
      });
      toast.success('Licence photo saved · shown publicly on your storefront');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save licence photo');
    } finally { setBusy(false); }
  };

  const alreadySaved = !!store.license_photo_url;
  const isMedical = store.category === 'medical';

  return (
    <section data-testid="store-license-uploader" className="card-soft p-5 mb-5">
      <div className="flex items-center gap-2">
        <ScrollText className="w-4 h-4 text-[#1B5E20]"/>
        <div className="font-heading font-bold text-lg">Shop licence — public proof</div>
        {alreadySaved && (
          <span data-testid="license-saved-pill" className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] border border-[#A5D6A7] text-[#1B5E20] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
            <CheckCircle2 className="w-3 h-3"/>On-file
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-ink/70">
        Snap a pic of the licence hanging on your shop wall — customers see it on your storefront so they know you&apos;re the real deal.
        {isMedical && <b className="text-[#B71C1C]"> Medical shops must display the Drug Licence.</b>}
      </p>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Wall-licence photo</div>
          <ImageUploader
            testId="license-photo-uploader"
            label="wall-license photo"
            value={url}
            onChange={setUrl}
          />
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Licence number (as printed)</div>
            <input
              data-testid="license-number-input"
              value={number}
              onChange={e => setNumber(e.target.value)}
              placeholder="e.g. FSSAI-10012345 / DL-KL-B-123"
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none font-mono"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Issuing authority</div>
            <input
              data-testid="license-authority-input"
              value={authority}
              onChange={e => setAuthority(e.target.value)}
              placeholder={isMedical ? 'Drugs Control Dept, State' : 'FSSAI / Shops & Establishments'}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none"
            />
          </div>
          <button
            data-testid="save-license-photo"
            onClick={save}
            disabled={busy || !url}
            className="btn-primary w-full disabled:opacity-50"
          >
            {busy ? 'Saving…' : (alreadySaved ? 'Update licence photo' : 'Publish licence photo')}
          </button>
        </div>
      </div>
    </section>
  );
}
